import type { BotMode } from '/imports/api/botModes';

/**
 * Pure lifecycle logic for the desired-state design: the display matrix, the
 * staleness rule, and the supervisor's batch selection. Meteor-free so the
 * stall monitor, the watchdog and the UI all derive from ONE definition of
 * each rule -- and so all three are unit-testable.
 */

export type DesiredState = 'up' | 'down';

/** How long an operational status may go unstamped before the row is treated
 *  as a claim rather than an observation. Three missed heartbeats. */
export const STALE_MS = 90_000;

/** How often the botrunner stamps lastSeenAt for its live bots. */
export const HEARTBEAT_MS = 30_000;

/** Bots the fleet needs up before trade-facing bots start replenishing from
 *  them: bankers, depots and the census. Start ordering puts them first. */
export const infraModes: BotMode[] = ['banker', 'depot', 'shredDepot', 'census'];

const OPERATIONAL = new Set(['connecting', 'loggingIn', 'running', 'inTrade', 'stalled', 'restarting']);

export const isOperationalStatus = (status: string | undefined): boolean =>
  !!status && OPERATIONAL.has(status);

/** A status row is stale when nothing has stamped it within STALE_MS. A row
 *  never stamped at all (absent lastSeenAt) is stale by definition -- nothing
 *  is observing it. Only meaningful for operational statuses; offline/crashed
 *  rows are terminal observations and never "go quiet". */
export const isStale = (lastSeenAt: Date | undefined | null, now: number): boolean =>
  !lastSeenAt || now - lastSeenAt.getTime() > STALE_MS;

export type DisplayTone = 'ok' | 'busy' | 'pending' | 'recovering' | 'fault' | 'idle' | 'stale';

export interface BotDisplay {
  label: string;
  tone: DisplayTone;
}

/**
 * The desired x status display matrix (design doc §6). Every in-between pair
 * gets a name, so what today renders as confusion reads as a state:
 * transition states are expected and quiet; persistent disagreement is a
 * fault and says so. Staleness overrides everything -- a stale operational
 * claim is displayed as unverified, whatever it claims.
 */
export const deriveBotDisplay = (
  desired: DesiredState | undefined,
  status: string | undefined,
  stale: boolean,
): BotDisplay => {
  const operational = isOperationalStatus(status);
  if (operational && stale) return { label: `${status} (stale)`, tone: 'stale' };
  if (desired === 'up') {
    if (status === 'running') return { label: 'running', tone: 'ok' };
    if (status === 'inTrade') return { label: 'in trade', tone: 'busy' };
    if (operational) return { label: status!, tone: 'pending' };
    if (status === 'stalled' || status === 'crashed') return { label: `${status} — recovering`, tone: 'recovering' };
    return { label: 'start pending', tone: 'pending' };
  }
  if (desired === 'down') {
    if (operational) return { label: `${status} — stopping`, tone: 'fault' };
    return { label: 'stopped', tone: 'idle' };
  }
  // No desired state recorded (pre-migration doc): show the raw status.
  return { label: status ?? 'unknown', tone: operational ? 'ok' : 'idle' };
};

export interface SupervisableBot {
  username: string;
  mode?: BotMode | string;
  desiredState?: DesiredState;
  status?: string;
  active?: boolean;
}

/**
 * Which bots the supervisor should start this tick, in order.
 *
 * Desired-up, active, and fully offline -- stalled/crashed stay with the
 * restart path, and anything operational is already on its way. Infra modes
 * (bankers, depots, census) sort first because trade-facing bots replenish
 * from them the moment they come up. Capped at `limit` per tick so a fleet
 * coming back from a botrunner restart logs in as a paced trickle, not the
 * simultaneous-login burst that got the egress IP rate-limited.
 */
export const supervisorBatch = <B extends SupervisableBot>(bots: B[], limit: number): B[] => {
  const rank = (b: B) => (infraModes.includes(b.mode as BotMode) ? 0 : 1);
  return bots
    .filter((b) => b.active !== false && b.desiredState === 'up' && b.status === 'offline')
    .sort((a, b) => rank(a) - rank(b) || a.username.localeCompare(b.username))
    .slice(0, Math.max(0, limit));
};
